(() => {
  const STORAGE_KEY = 'ct_wishlist_v1';
  const BUTTON_SELECTOR = '[data-ct-wishlist-button]';
  const UPDATED_EVENT = 'ct:wishlist:updated';
  const MAX_WISHLIST_ITEMS = 200;

  class CTWishlistStore {
    read() {
      try {
        const storedValue = window.localStorage.getItem(
          STORAGE_KEY
        );

        if (!storedValue) {
          return [];
        }

        const parsedValue = JSON.parse(storedValue);

        if (!Array.isArray(parsedValue)) {
          return [];
        }

        return [
          ...new Set(
            parsedValue
              .filter(
                (handle) =>
                  typeof handle === 'string'
              )
              .map((handle) =>
                handle.trim().toLowerCase()
              )
              .filter(
                (handle) =>
                  handle.length > 0 &&
                  handle.length <= 255 &&
                  /^[a-z0-9][a-z0-9-]*$/.test(
                    handle
                  )
              )
          ),
        ].slice(0, MAX_WISHLIST_ITEMS);
      } catch (error) {
        console.warn(
          'Chubbytrend Wishlist: Could not read storage.',
          error
        );

        return [];
      }
    }

    write(handles) {
      const sanitizedHandles = [
        ...new Set(
          handles
            .filter(
              (handle) =>
                typeof handle === 'string'
            )
            .map((handle) =>
              handle.trim().toLowerCase()
            )
            .filter(
              (handle) =>
                handle.length > 0 &&
                handle.length <= 255 &&
                /^[a-z0-9][a-z0-9-]*$/.test(
                  handle
                )
            )
        ),
      ].slice(0, MAX_WISHLIST_ITEMS);

      try {
        window.localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify(sanitizedHandles)
        );
      } catch (error) {
        console.warn(
          'Chubbytrend Wishlist: Could not write storage.',
          error
        );

        return false;
      }

      this.dispatchUpdate(sanitizedHandles);

      return true;
    }

    has(handle) {
      return this.read().includes(handle);
    }

    add(handle) {
      const handles = this.read();

      if (!handles.includes(handle)) {
        handles.push(handle);
      }

      return this.write(handles);
    }

    remove(handle) {
      const handles = this.read().filter(
        (savedHandle) => savedHandle !== handle
      );

      return this.write(handles);
    }

    toggle(handle) {
      const isSaved = this.has(handle);

      if (isSaved) {
        this.remove(handle);
        return false;
      }

      this.add(handle);
      return true;
    }

    clear() {
      return this.write([]);
    }

    dispatchUpdate(handles = this.read()) {
      document.dispatchEvent(
        new CustomEvent(UPDATED_EVENT, {
          detail: {
            handles,
            count: handles.length,
          },
        })
      );
    }
  }

  const wishlistStore = new CTWishlistStore();

  function updateButton(button) {
    const handle = button.dataset.productHandle;

    if (!handle) {
      return;
    }

    const isActive = wishlistStore.has(handle);
    const productTitle =
      button.dataset.productTitle || 'Product';

    const addLabel =
      button.dataset.addLabel || 'Add to wishlist';

    const removeLabel =
      button.dataset.removeLabel ||
      'Remove from wishlist';

    const activeLabel = isActive
      ? removeLabel
      : addLabel;

    button.classList.toggle('is-active', isActive);

    button.setAttribute(
      'aria-pressed',
      String(isActive)
    );

    button.setAttribute(
      'aria-label',
      `${activeLabel}: ${productTitle}`
    );

    button.setAttribute('title', activeLabel);

    const hiddenLabel = button.querySelector(
      '[data-ct-wishlist-button-label]'
    );

    if (hiddenLabel) {
      hiddenLabel.textContent = activeLabel;
    }
  }

  function updateAllButtons(root = document) {
    root
      .querySelectorAll(BUTTON_SELECTOR)
      .forEach((button) => {
        updateButton(button);
      });
  }

  function handleWishlistClick(event) {
    const button = event.target.closest(
      BUTTON_SELECTOR
    );

    if (!button) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const handle = button.dataset.productHandle;

    if (!handle) {
      return;
    }

    const isNowActive =
      wishlistStore.toggle(handle);

    /*
     * Update every button for the same product.
     * This keeps collection cards, sliders and recently
     * viewed sections synchronized.
     */
    updateAllButtons();

    button.classList.remove('is-animating');

    window.requestAnimationFrame(() => {
      button.classList.add('is-animating');

      window.setTimeout(() => {
        button.classList.remove('is-animating');
      }, 360);
    });

    document.dispatchEvent(
  new CustomEvent('ct:wishlist:toggled', {
    detail: {
      handle,
      productTitle:
        button.dataset.productTitle || 'Product',
      isProductPage: button.classList.contains(
        'ct-wishlist-button--product'
      ),
      isActive: isNowActive,
      count: wishlistStore.read().length,
    },
  })
);
  }

  function initializeWishlist(root = document) {
    updateAllButtons(root);

    wishlistStore.dispatchUpdate();
  }

  document.addEventListener(
    'click',
    handleWishlistClick
  );

  document.addEventListener(
    'DOMContentLoaded',
    () => {
      initializeWishlist();
    }
  );

  /*
   * Reinitialize buttons added through the Shopify
   * Theme Editor.
   */
  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeWishlist(event.target);
    }
  );

  /*
   * Synchronize Wishlist when another browser tab
   * changes Local Storage.
   */
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY) {
      return;
    }

    updateAllButtons();
    wishlistStore.dispatchUpdate();
  });

  /*
   * Public interface for the Wishlist page,
   * header counter and future account sync adapter.
   */
  window.CTWishlist = {
    getHandles() {
      return wishlistStore.read();
    },

    replace(handles) {
    const safeHandles = Array.isArray(handles)
      ? handles
      : [];

    const result = wishlistStore.write(
      safeHandles
    );

    updateAllButtons();

    return result;
    },

    has(handle) {
      return wishlistStore.has(handle);
    },

    add(handle) {
      const result = wishlistStore.add(handle);
      updateAllButtons();
      return result;
    },

    remove(handle) {
      const result = wishlistStore.remove(handle);
      updateAllButtons();
      return result;
    },

    toggle(handle) {
      const result = wishlistStore.toggle(handle);
      updateAllButtons();
      return result;
    },

    clear() {
      const result = wishlistStore.clear();
      updateAllButtons();
      return result;
    },

    refresh(root = document) {
      initializeWishlist(root);
    },
  };
})();
